import type { ReservationService } from '@eliadmin/core/src/api/reservation/reservation-api';
import {
  ReservationSlotType,
  type ReservationSlot,
} from '@eliadmin/core/src/api/reservation/reservation-api';
import { useTranslations } from '@eliadmin/core/src/hooks/use-translations';
import { addDays, addMinutes, format, startOfDay } from 'date-fns';
import { Button } from '@eliadmin/core/src/components/ui/button';
import { ReservationCalendarColumnSkeleton } from '@eliadmin/core/src/components/reservation/reservation-calendar/reservation-calendar-column-skeleton';
import { cn } from '@eliadmin/core/src/utils/style-utils';
import { useFormContext } from 'react-hook-form';
import type { ReservationFormSchemaType } from '@eliadmin/core/src/components/reservation/reservation-form';
import { useEffect, useMemo, useRef } from 'react';
import { getHolidays, type AvailableLocales } from '@eliadmin/core/src/utils/date-holidays-utils';
import { normalizeConfiguredDate, type SpecificDatesConfiguration } from './specific-dates';

const DaysOfTheWeekTranslateStrings = [
  'text1302',
  'text1303',
  'text1304',
  'text1305',
  'text1306',
  'text1307',
  'text1308',
];

type ReservationCalendarSlotsProps = {
  slots?: ReservationSlot[][];
  selectedService?: ReservationService;
  isSlotsLoading: boolean;
  direction?: 'row' | 'column';
  dateFrom: Date;
  locale: string;
  specificDatesConfiguration?: SpecificDatesConfiguration;
};

export const ReservationCalendarSlots = ({
  slots,
  selectedService,
  isSlotsLoading,
  direction = 'row',
  dateFrom,
  locale,
  specificDatesConfiguration,
}: ReservationCalendarSlotsProps) => {
  const { t } = useTranslations();
  const form = useFormContext<ReservationFormSchemaType>();
  const selectedSlotRef = useRef<HTMLButtonElement | null>(null);

  const noAvailableSlots = slots?.every(day => day.length === 0);
  const holidayKeySet = useMemo(() => {
    try {
      const holidayDates = getHolidays(locale as AvailableLocales);
      return new Set(holidayDates.map(d => format(startOfDay(d), 'yyyy-MM-dd')));
    } catch {
      return new Set<string>();
    }
  }, [locale]);

  const alwaysOpenKeySet = useMemo(() => {
    return new Set(
      (specificDatesConfiguration?.always_open ?? [])
        .map(normalizeConfiguredDate)
        .filter((value): value is string => value !== null),
    );
  }, [specificDatesConfiguration?.always_open]);

  const alwaysClosedKeySet = useMemo(() => {
    return new Set(
      (specificDatesConfiguration?.always_closed ?? [])
        .map(normalizeConfiguredDate)
        .filter((value): value is string => value !== null),
    );
  }, [specificDatesConfiguration?.always_closed]);

  const getDayKey = (index: number) => {
    return format(startOfDay(addDays(dateFrom, index)), 'yyyy-MM-dd');
  };

  const isDayHidden = (index: number) => {
    const dayKey = getDayKey(index);

    if (alwaysOpenKeySet.has(dayKey)) {
      return false;
    }

    if (alwaysClosedKeySet.has(dayKey)) {
      return true;
    }

    return holidayKeySet.has(dayKey);
  };

  const handleSetSelectedTime = (
    slot: ReservationSlot,
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    allSlotsFromSelectedDay: ReservationSlot[],
  ) => {
    form.setValue('reserved_at', String(slot.datetime_start));
  };

  const reserved_at = form.watch('reserved_at');

  const selectedRange = useMemo(() => {
    if (!reserved_at || !selectedService) return undefined;

    const reserved_at_date = new Date(reserved_at);
    const endDate = addMinutes(reserved_at_date, selectedService.duration);

    return {
      startDate: reserved_at_date,
      endDate: endDate,
    };
  }, [reserved_at, selectedService]);

  const isSlotInRange = (slot: ReservationSlot) => {
    if (!selectedRange) return false;

    const slotDate = new Date(slot.datetime_start);
    return slotDate >= selectedRange.startDate && slotDate < selectedRange.endDate;
  };

  useEffect(() => {
    if (isSlotsLoading || !reserved_at) return;

    selectedSlotRef.current?.scrollIntoView({
      behavior: 'smooth',
      block: 'center',
      inline: 'center',
    });
  }, [isSlotsLoading, reserved_at, slots]);

  return (
    <>
      {/* Mobile (<md): row layout (day label on the left, times scroll horizontally) */}
      <div
        className={cn(
          'flex max-h-[500px] w-full flex-col gap-3 overflow-y-auto md:hidden',
          'ea-reservation-calendar-slots',
        )}
      >
        <div className={cn('flex w-full gap-2', noAvailableSlots && 'hidden')}>
          {/* Day labels (fixed) */}
          <div className="flex w-16 flex-shrink-0 flex-col gap-3">
            {DaysOfTheWeekTranslateStrings.map((day, index) => (
              <div
                key={index}
                className={cn(
                  'flex h-[36px] select-none items-center justify-center rounded-[8px] bg-primary-foreground text-sm font-medium text-black',
                  'ea-reservation-calendar-slots__day-name-cell',
                )}
              >
                {t(day).toUpperCase().slice(0, 3)}
              </div>
            ))}
          </div>

          {/* Times (single shared horizontal scroll) */}
          <div className="min-w-0 flex-1 overflow-x-auto">
            <div className="flex w-max flex-col gap-3 pr-2">
              {DaysOfTheWeekTranslateStrings.map((day, index) => (
                <div
                  key={day}
                  className={cn(
                    'flex h-[36px] items-center gap-2',
                    'ea-reservation-calendar-slots__day-row',
                  )}
                >
                  {isSlotsLoading
                    ? Array.from({ length: 4 }).map((_, i) => (
                        <div
                          key={i}
                          className="h-[36px] w-20 animate-pulse rounded-[8px] bg-black/10"
                        />
                      ))
                    : null}

                  {!isSlotsLoading && isDayHidden(index) ? (
                    // Holiday: don't show any slots for this day.
                    <div className="h-[36px] w-1" />
                  ) : slots?.[index] && !isSlotsLoading ? (
                    slots[index].map((slot, slotIndex) => (
                      <Button
                        ref={isSlotInRange(slot) ? selectedSlotRef : undefined}
                        key={slotIndex}
                        type="button"
                        className={cn(
                          'h-[36px] w-20 cursor-default rounded-[8px] border border-black/10 py-0 text-center text-sm font-medium',
                          'ea-reservation-calendar-slots__slot',
                          slot.type === ReservationSlotType.FREE &&
                            'ea-reservation-calendar-slots__free cursor-pointer bg-transparent text-black hover:bg-black/5',
                          isSlotInRange(slot) && 'bg-black text-white hover:bg-black/90',
                          slot.type === ReservationSlotType.BREAK &&
                            'ea-reservation-calendar-slots__break bg-yellow-500 text-yellow-300 hover:bg-yellow-500',
                          slot.type === ReservationSlotType.RESERVED &&
                            'ea-reservation-calendar-slots__reserved bg-destructive text-destructive-foreground hover:bg-destructive',
                          slot.type === ReservationSlotType.NONRESERVABLE &&
                            'ea-reservation-calendar-slots__nonreservable bg-black/50 text-white hover:bg-black/50',
                        )}
                        onClick={() =>
                          slot.type === ReservationSlotType.FREE &&
                          handleSetSelectedTime(slot, slots[index])
                        }
                      >
                        {format(new Date(slot.datetime_start), 'HH:mm')}
                      </Button>
                    ))
                  ) : !isSlotsLoading ? (
                    // Keep row height aligned even if there are no slots for the day.
                    <div className="h-[36px] w-1" />
                  ) : null}
                </div>
              ))}
            </div>
          </div>
        </div>

        {noAvailableSlots && (
          <div className="flex items-center justify-center py-16 text-2xl font-bold">
            {t('text1310')}
          </div>
        )}
      </div>

      {/* Desktop (md+): keep existing implementation */}
      <div className="hidden md:block">
        <div
          className={cn(
            direction === 'row'
              ? 'flex flex-col flex-wrap gap-3 overflow-y-scroll sm:w-full'
              : 'grid max-h-[500px] min-h-[400px] grid-cols-[repeat(7,90px)] gap-3 overflow-auto sm:w-full',
            'ea-reservation-calendar-slots',
          )}
        >
          {DaysOfTheWeekTranslateStrings.map((day, index) => (
            <div
              key={index}
              className={cn(
                'flex gap-1 sm:gap-3',
                direction === 'row' ? 'flex-row' : 'flex-col',
                direction !== 'row' && 'w-[90px]',
                noAvailableSlots && 'hidden sm:block',
                'ea-reservation-calendar-slots__day-column',
              )}
            >
              <div className="sticky top-0 bg-white">
                <div
                  className={cn(
                    'hidden h-[36px] select-none items-center justify-center rounded-[8px] bg-primary-foreground text-sm font-medium text-black sm:flex',
                    direction === 'row' && 'w-16',
                    'ea-reservation-calendar-slots__day-name-cell',
                  )}
                >
                  {t(day)}
                </div>
              </div>
              <div
                className={cn(
                  'sticky left-0 z-50 flex h-[50px] select-none items-center justify-center rounded-[4px] border border-border bg-primary font-bold text-primary-foreground sm:hidden',
                  direction === 'row' && 'w-16',
                  'ea-reservation-calendar-slots__day-name-cell',
                )}
              >
                {t(day).toUpperCase().slice(0, 3)}
              </div>
              {isSlotsLoading && (
                <ReservationCalendarColumnSkeleton
                  className={{
                    wrapper: cn(direction === 'row' && 'flex-row'),
                  }}
                />
              )}
              {!isSlotsLoading && isDayHidden(index)
                ? null
                : slots?.[index] && !isSlotsLoading
                  ? slots[index].map((slot, slotIndex) => (
                      <Button
                        ref={isSlotInRange(slot) ? selectedSlotRef : undefined}
                        key={slotIndex}
                        type="button"
                        className={cn(
                          'h-[36px] cursor-default rounded-[8px] border border-black/10 py-0 text-center text-sm font-medium',
                          'ea-reservation-calendar-slots__slot',
                          direction === 'row' && 'w-20 sm:w-28',
                          slot.type === ReservationSlotType.FREE &&
                            'ea-reservation-calendar-slots__free cursor-pointer bg-transparent text-black hover:bg-black/5',
                          isSlotInRange(slot) && 'bg-black text-white hover:bg-black/90',
                          slot.type === ReservationSlotType.BREAK &&
                            'ea-reservation-calendar-slots__break bg-yellow-500 text-yellow-300 hover:bg-yellow-500',
                          slot.type === ReservationSlotType.RESERVED &&
                            'ea-reservation-calendar-slots__reserved bg-destructive text-destructive-foreground hover:bg-destructive',
                          slot.type === ReservationSlotType.NONRESERVABLE &&
                            'ea-reservation-calendar-slots__nonreservable bg-black/50 text-white hover:bg-black/50',
                        )}
                        onClick={() =>
                          slot.type === ReservationSlotType.FREE &&
                          handleSetSelectedTime(slot, slots[index])
                        }
                      >
                        {format(new Date(slot.datetime_start), 'HH:mm')}
                      </Button>
                    ))
                  : null}
            </div>
          ))}
          {noAvailableSlots && (
            <div className="col-span-7 flex items-center justify-center py-16 text-2xl font-bold">
              {t('text1310')}
            </div>
          )}
        </div>
      </div>
    </>
  );
};
